import { Dialog as BaseUiDialog } from '@base-ui-components/react/dialog';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import { match } from 'ts-pattern';

import type { CommonProps } from '#/ui/utils/types';

export interface DialogProps extends CommonProps {
  /**
   * The content of the dialog.
   */
  children?: React.ReactNode;
  /**
   * The trigger element for the dialog.
   */
  trigger?: React.ReactNode;
  /**
   * The variant of the dialog.
   * @default 'plain'
   */
  variant?: 'plain' | 'card';
  /**
   * If true, the dialog is open, this turns the component into a controlled component and you can't
   * use the DialogTrigger component.
   * @default undefined
   */
  open?: boolean;
  /**
   * If true, the dialog will be open by default.
   * @default false
   */
  defaultOpen?: boolean;
  /**
   * Callback fired when the component requests to be closed.
   * @param {object} event The event source of the callback.
   */
  onClose?: () => void;
  /**
   * If true, the dialog will be full screen.
   * @default false
   */
  fullScreen?: boolean;
  /**
   * The max width of the dialog.
   * @default 'sm'
   */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  /**
   * If true, the backdrop will not close the dialog.
   * @default false
   */
  disableBackdropClick?: boolean;
}

function Dialog({
  children,
  trigger,
  open,
  defaultOpen = false,
  onClose,
  fullScreen = false,
  maxWidth = 'sm',
  disableBackdropClick = false,
}: DialogProps) {
  const handleBackdropClick = React.useCallback(() => {
    if (!disableBackdropClick) {
      onClose?.();
    }
  }, [disableBackdropClick, onClose]);
  return (
    <BaseUiDialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={onClose}>
      {trigger}
      <BaseUiDialog.Portal>
        <div className='d-modal d-modal-open z-dialog'>
          <BaseUiDialog.Backdrop className='modal-backdrop' onClick={handleBackdropClick} />
          <BaseUiDialog.Popup
            className={twMerge(
              'd-modal-box w-auto min-w-screen-lg',
              match({ maxWidth, fullScreen })
                .with(
                  { fullScreen: true },
                  () =>
                    'h-screen w-screen max-w-full rounded-none [&_.gooey-dialog-actions]:absolute [&_gooey-dialog-actions]:bottom-0 [&_.gooey-dialog-actions]:right-0 [&_.gooey-dialog-actions]:px-8'
                )
                .when(
                  (x) => x.maxWidth,
                  (x) => `${maxWidthMap[x.maxWidth]}`
                )
                .otherwise(() => 'max-w-sm')
            )}
          >
            {children}
          </BaseUiDialog.Popup>
        </div>
      </BaseUiDialog.Portal>
    </BaseUiDialog.Root>
  );
}
Dialog.displayName = 'GooeyDialog';

export default Dialog;

/*
|------------------
| Internals
|------------------
*/

const maxWidthMap = {
  xs: 'max-w-screen-xs min-w-7/8 sm:min-w-sm',
  sm: 'max-w-screen-sm min-w-7/8 sm:min-w-sm',
  md: 'max-w-screen-md min-w-7/8 md:min-w-md',
  lg: 'max-w-screen-lg min-w-7/8 md:min-w-lg',
  xl: 'max-w-screen-xl min-w-7/8 md:min-w-xl',
  '2xl': 'max-w-screen-2xl min-w-7/8 md:min-w-2xl',
  full: 'max-w-7/8 min-w-7/8 md:min-w-3xl',
} as const;
